<script setup lang="ts">
import { getStockComponents, getStockDetail } from '@/api/modules/market'
import charts from './components/charts.vue'

definePage({
  name: 'market-detail',
  meta: {
    title: '股票详情',
  },
})

interface StockDetail {
  name: string
  code: string
  price: number
  change: number
  changeRate: number
  open: string
  prevClose: number
  high: string
  low: string
  volume: string
  amplitude: string
  updateTime: string
}

interface ComponentStock {
  name: string
  code: string
  price: number
  changeRate: number
  isFavorite: boolean
}

const route = useRoute()
const router = useRouter()
function onSwitchPage(name: any) {
  router.push({ name })
}

const activeTab = ref<'quote' | 'news'>('quote')
const activePeriod = ref<string>('年K')

// 根据上一级路由决定底部按钮
const fromRoute = computed(() => {
  const from = route.query.from as string
  if (from) {
    return from
  }
  // 如果没有from参数，尝试从referrer获取
  if (typeof window !== 'undefined' && document.referrer) {
    try {
      const referrerUrl = new URL(document.referrer)
      return referrerUrl.pathname
    }
    catch {
      return ''
    }
  }
  return ''
})

const show = ref(false)

// 判断是否从自选页面跳转过来
const isFromSelected = computed(() => {
  return fromRoute.value.includes('/selected')
})

const stockDetail = ref<StockDetail>({
  name: '沪港通南向',
  code: 'HKDQB.SH',
  price: 42000.00,
  change: 0.00,
  changeRate: 0.00,
  open: '--',
  prevClose: 42000.00,
  high: '--',
  low: '--',
  volume: '--',
  amplitude: '--',
  updateTime: '12-26 09:01:38',
})

const componentStocks = ref<ComponentStock[]>([])
const showDropdown = ref(false)
const showPrompt = ref(true)

const periodOptions = ['1分', '5分', '15分', '30分', '60分', '季K', '年K']

function formatPrice(value: number) {
  return value.toFixed(2)
}

function formatSignedNumber(value: number) {
  return value > 0 ? `+${value.toFixed(2)}` : value.toFixed(2)
}

function getChangeClass(value: number) {
  if (value > 0) {
    return 'is-up'
  }
  if (value < 0) {
    return 'is-down'
  }
  return ''
}

async function loadData() {
  try {
    const params = route.params as Record<string, string>
    const query = route.query as Record<string, string>
    const code = params.code || query.code || 'HKDQB.SH'
    const [detailRes, componentsRes] = await Promise.all([
      getStockDetail(code),
      getStockComponents(code),
    ])

    if (detailRes.status === 1) {
      stockDetail.value = detailRes.data
    }
    if (componentsRes.status === 1) {
      componentStocks.value = componentsRes.data
    }
  }
  catch (error) {
    console.error('加载股票详情失败:', error)
  }
}

onMounted(() => {
  loadData()
  // 添加测试数据
  componentStocks.value = [
    {
      name: '百奥赛图 - B',
      code: '02315.HK',
      price: 35.08,
      changeRate: 22.23,
      isFavorite: false,
    },
    {
      name: '百奥赛图 - B',
      code: '02315.HK',
      price: 35.08,
      changeRate: 22.23,
      isFavorite: false,
    },
    {
      name: '百奥赛图 - B',
      code: '02315.HK',
      price: 35.08,
      changeRate: 22.23,
      isFavorite: true,
    },
  ]
})

const more = ref(false)
const checked = ref('1')
const showAdd = ref(false)
</script>

<template>
  <FmPageLayout navbar :tabbar="false" back>
    <template #navbar>
      <div>
        <div class="m-t-4px text-18px font-500">
          {{ stockDetail.name }}({{ stockDetail.code }})
        </div>
        <div class="m-t-4px text-11px line-height-11px">
          {{ stockDetail.updateTime }}
        </div>
      </div>
    </template>
    <div class="sd">
      <div class="sd-notice">
        港股行情至少延时15分钟,购买港股行情
      </div>

      <div class="sd-tabs">
        <div class="sd-tabs__item" :class="{ active: activeTab === 'quote' }" @click="activeTab = 'quote'">
          报价
        </div>
        <div class="sd-tabs__item" :class="{ active: activeTab === 'news' }" @click="activeTab = 'news'">
          新闻
        </div>
      </div>

      <template v-if="activeTab === 'quote'">
        <div class="sd-quote">
          <div class="sd-quote__main" :class="getChangeClass(stockDetail.changeRate)">
            <span class="sd-quote__price">{{ formatPrice(stockDetail.price) }}</span>
            <span class="sd-quote__change">&nbsp;{{ formatSignedNumber(stockDetail.change) }}&nbsp;&nbsp;{{ formatSignedNumber(stockDetail.changeRate) }}%</span>
          </div>

          <van-row class="p-10px" :gutter="[10, 5]">
            <van-col span="8">
              <div class="flex justify-between text-11px">
                <div class="w-50px">
                  今开
                </div>
                <div>--</div>
              </div>
            </van-col>
            <van-col span="8">
              <div class="flex justify-between text-11px">
                <div class="w-50px">
                  最高
                </div>
                <div>--</div>
              </div>
            </van-col>
            <van-col span="8">
              <div class="flex justify-between text-11px">
                <div class="w-50px">
                  成交额
                </div>
                <div>--</div>
              </div>
            </van-col>
            <van-col span="8">
              <div class="flex justify-between text-11px">
                <div class="w-50px">
                  昨收
                </div>
                <div>4200.00</div>
              </div>
            </van-col>
            <van-col span="8">
              <div class="flex justify-between text-11px">
                <div class="w-50px">
                  最低
                </div>
                <div>--</div>
              </div>
            </van-col>
            <van-col span="8">
              <div class="flex justify-between text-11px">
                <div class="w-50px">
                  振幅
                </div>
                <div>--</div>
              </div>
            </van-col>
          </van-row>

          <div class="sd-quote__expand">
            <FmIcon name="material-symbols:keyboard-arrow-down" />
          </div>
        </div>

        <div v-if="showPrompt" class="sd-prompt">
          <FmIcon name="solar:chat-round-line-outline" class="text-15px color-[#3C78FF]" />

          <div class="sd-prompt__text">
            及时互动新方式,在线畅聊抓时机。
          </div>
          <div class="sd-prompt__close" @click="showPrompt = false">
            <FmIcon name="material-symbols:close" />
          </div>
        </div>

        <div class="sd-chart p-t-30px">
          <div class="sd-chart__periods">
            <div
              v-for="period in ['分时', '5日', '日K', '周K', '月K']"
              :key="period"
              class="sd-chart__period"
              :class="{ active: activePeriod === period }"
              @click="activePeriod = period"
            >
              {{ period }}
            </div>
            <div class="sd-chart__dropdown" @click="showDropdown = !showDropdown">
              <span>年K</span>
              <FmIcon name="material-symbols:keyboard-arrow-down" class="icoin" />
              <div v-if="showDropdown" class="sd-chart__dropdown-menu">
                <div
                  v-for="option in periodOptions"
                  :key="option"
                  class="sd-chart__dropdown-item"
                  :class="{ active: activePeriod === option }"
                  @click.stop="activePeriod = option; showDropdown = false"
                >
                  {{ option }}
                </div>
              </div>
            </div>
          </div>

          <charts style="height: 400px;" />
        </div>
        <div>
          <vxe-table
            header-row-class-name="table-header"
            :data="componentStocks"
          >
            <vxe-column field="name" title="名称/代码" width="30%">
              <template #default="{ row }">
                <div class="van-ellipsis text-15px font-500">
                  {{ row.name }}
                </div>
                <div class="text-13px">
                  {{ row.code }}
                </div>
              </template>
            </vxe-column>
            <vxe-column field="price" title="现价" align="center" width="40%" />
            <vxe-column field="changeRate" width="30%" title="涨跌幅" align="right" sortable>
              <template #default="{ row }">
                <div class="rounded-5px bg-[#E51A2D] p-l-9px p-r-9px text-center color-[#fff] line-height-29px">
                  +{{ row.changeRate }}%
                </div>
              </template>
            </vxe-column>
          </vxe-table>
        </div>
      </template>

      <template v-else>
        <div v-for="value in 7" :key="value" class="bg-[#fff]" style="padding: 10px 7px; border-bottom: 1px solid #e4e5e9;">
          <div class="m-b-5px text-15px font-500">
            瑞银：重视南向资金增加带来的港股机遇
          </div>
          <div class="text-11px color-[#666]">
            钛媒体｜08-13 14:45
          </div>
        </div>
      </template>
    </div>

    <!-- 底部导航栏 -->
    <template v-if="activeTab === 'quote'">
      <div v-if="!more" class="sd-navbar">
        <div class="sd-navbar__item">
          <FmIcon name="ri:share-box-fill" class="text-21px" />
          <span>分享</span>
        </div>
        <div v-if="!isFromSelected" class="sd-navbar__item">
          <FmIcon name="material-symbols:add-box-outline" class="text-21px" />
          <span>添加自选</span>
        </div>
        <div v-if="isFromSelected" class="sd-navbar__item">
          <FmIcon name="material-symbols:remove" class="text-21px" />
          <span>删除自选</span>
        </div>
        <div class="sd-navbar__item">
          <FmIcon name="material-symbols:notifications-outline" class="text-21px" />
          <span>提醒</span>
        </div>
        <div class="sd-navbar__item" @click="more = true">
          <FmIcon name="material-symbols:apps" class="text-21px" />
          <span>更多</span>
        </div>
      </div>
      <div v-else class="sd-navbar" style="padding-bottom: 60px;">
        <div class="sd-navbar__item" @click="onSwitchPage('quotation-comments')">
          <FmIcon name="majesticons:chat-2-text-line" class="text-21px" />
          <span>评论</span>
        </div>
        <div class="sd-navbar__item" @click="onSwitchPage('remark')">
          <FmIcon name="icon-park-twotone:edit-two" class="text-21px" />
          <span>备注</span>
        </div>
        <div class="sd-navbar__item" @click="show = true">
          <FmIcon name="material-symbols:ad-group-outline" class="text-21px" />
          <span>分组</span>
        </div>

        <div style="border-top: 1px solid #e4e5e9;" class="absolute bottom-0 left-0 h-45px w-100% text-center color-[#333] line-height-45px" @click="more = false">
          取消
        </div>
      </div>

      <van-popup v-model:show="show" position="bottom" :style="{ }">
        <div class="h-30px flex items-center justify-center">
          <div class="h-3px w-37px bg-[#999]" style="border-radius: 3px;" />
        </div>
        <div class="p-l-20px text-17px line-height-30px">
          编辑分组
        </div>
        <div class="m-b-20px p-l-20px">
          <van-radio-group v-model="checked" shape="dot">
            <van-radio name="1" class="m-b-10px m-t-10px">
              分组 1
            </van-radio>
            <van-radio name="2" class="m-b-10px m-t-10px">
              分组 2
            </van-radio>
            <van-radio name="3" class="m-b-10px m-t-10px">
              分组 3
            </van-radio>
          </van-radio-group>
        </div>
        <div class="h-30px flex items-center p-b-20px p-l-20px color-[#3C78FF]" @click="showAdd = true">
          <FmIcon name="material-symbols-light:add" class="text-21px" />
          <div>新建分组</div>
        </div>
        <div class="m-b-30px flex items-center justify-between gap-10px p-l-20px p-r-20px">
          <van-button size="small" class="flex-1" plain type="primary" round @click="show = false">
            取消
          </van-button>
          <van-button size="small" class="flex-1" type="primary" round @click="show = false">
            确定
          </van-button>
        </div>
      </van-popup>

      <van-dialog v-model:show="showAdd" title="新建分组" show-cancel-button>
        <div class="m-t-30px flex items-center justify-center p-l-20px p-r-20px">
          <input type="text" class="h-37px w-100% p-l-10px" style="border: 1px solid #bfbfbf;border-radius: 7px;">
        </div>
        <div class="m-b-30px m-t-10px p-l-20px text-13px">
          0/20
        </div>
      </van-dialog>
    </template>
  </FmPageLayout>
</template>

<style lang="scss" scoped>
.sd-status-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 16px 0;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  background-color: #3c78ff;
}

.sd-status-bar__right {
  display: flex;
  gap: 4px;
  align-items: center;
}

.sd-status-bar__signal,
.sd-status-bar__wifi,
.sd-status-bar__battery {
  width: 17px;
  height: 10px;
  background-color: #fff;
  border-radius: 2px;
}

.sd-status-bar__battery {
  position: relative;

  &::after {
    position: absolute;
    top: 2px;
    right: -3px;
    width: 2px;
    height: 6px;
    content: "";
    background-color: #fff;
    border-radius: 0 1px 1px 0;
  }
}

.sd-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px 12px;
  color: #fff;
  background-color: #3c78ff;
}

.sd-header__back {
  display: flex;
  align-items: center;
  font-size: 24px;
  color: #fff;
  cursor: pointer;
}

.sd-header__content {
  flex: 1;
  text-align: center;
}

.sd-header__title {
  margin-bottom: 4px;
  font-size: 16px;
  font-weight: 600;
}

.sd-header__time {
  font-size: 12px;
  opacity: 0.9;
}

.sd-header__search {
  display: flex;
  align-items: center;
  font-size: 24px;
  color: #fff;
  cursor: pointer;
}

.sd-notice {
  font-size: 12px;
  line-height: 28px;
  color: #3579f8;
  text-align: center;
  background-color: #ffefe5;
  border-radius: 0;
}

.sd-tabs {
  display: flex;
  align-items: center;
  height: 32px;
  border-bottom: 1px solid #eef0f4;
}

.sd-tabs__item {
  margin: 0 10px;
  font-size: 15px;
  font-weight: 600;
  line-height: 32px;
  color: #8b95a5;
  text-align: center;
  cursor: pointer;
}

.sd-tabs__item.active {
  position: relative;
  font-weight: 700;
  color: #3c78ff;

  &::after {
    position: absolute;
    right: 0;
    bottom: 0;
    left: calc(50% - 5px);
    width: 10px;
    height: 2px;
    content: "";
    background-color: #3c78ff;
  }
}

.sd-quote {
  padding: 10px 11px;
  padding-bottom: 5px;
  background-color: #fff;
}

.sd-quote__main {
  width: 172px;
  height: 22px;
  margin: 0 214px 0 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0;
  line-height: 22px;
  color: #111e36;
  text-align: left;
  white-space: nowrap;
}

.sd-quote__main.is-up {
  color: #22a875;
}

.sd-quote__main.is-down {
  color: #e51a2d;
}

.sd-quote__price {
  font-size: 22px;
  font-weight: 400;
  line-height: 22px;
}

.sd-quote__change {
  font-size: 12px;
  font-weight: 400;
  line-height: 22px;
}

.sd-quote__metrics {
  display: flex;
  width: 370px;
  margin: 10px auto 0;
  white-space: nowrap;
}

.sd-quote__metric-group {
  display: flex;
  flex-direction: column;
}

.sd-quote__label {
  font-size: 12px;
  font-weight: 400;
  line-height: 12px;
  color: #111e36;
  text-align: left;
  white-space: nowrap;
}

.sd-quote__expand {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: #8b95a5;
  cursor: pointer;
}

.sd-prompt {
  display: flex;
  gap: 6px;
  align-items: center;
  height: 33px;
  padding: 0;
  padding: 0 10px;
  background-color: #fff;
  border-top: 1px solid #e4e5e9;
}

.sd-prompt__icon-wrapper {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;
  padding: 1px;
  background-color: #3c78ff;
  border: 1px solid #000;
}

.sd-prompt__icon-text {
  font-size: 10px;
  font-weight: 400;
  color: #000;
}

.sd-prompt__text {
  flex: 1;
  font-size: 12px;
  font-weight: 400;
  line-height: 12px;
  color: #333;
}

.sd-prompt__close {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  width: 12px;
  height: 12px;
  cursor: pointer;
}

.sd-chart {
  padding: 10px 7px;
  margin-bottom: 10px;
}

.sd-chart__periods {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}

.sd-chart__period {
  width: 54px;
  height: 22px;
  padding: 0 5px;
  font-size: 12px;
  font-weight: 400;
  color: #333;
  text-align: center;
  white-space: nowrap;
  cursor: pointer;
  background-color: transparent;
  border: 1px solid #e4e5e9;
  border-radius: 0;

  &.active {
    font-weight: 400;
    color: #fff;
    background-color: #3c78ff;
    border-color: #3c78ff;
  }
}

.sd-chart__dropdown {
  position: relative;
  gap: 0;
  width: 54px;
  height: 22px;
  padding: 0 5px;
  font-size: 12px;
  font-weight: 400;
  color: #fff;
  text-align: center;
  white-space: nowrap;
  cursor: pointer;
  background-color: #3c78ff;
  border: 1px solid #3c78ff;
  border-radius: 0;

  .icoin {
    position: absolute;
    top: calc(50% - 6px);
    right: 2px;
  }
}

.sd-chart__dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 100;
  min-width: 54px;
  margin-top: 4px;
  background-color: #faf8f9;
  border-right: 1px solid #e4e5e9;
  border-left: 1px solid #e4e5e9;
}

.sd-chart__dropdown-item {
  width: 54px;
  height: 22px;
  padding: 0 5px;
  font-size: 12px;
  font-weight: 400;
  color: #333;
  border-bottom: 1px solid #e4e5e9;
}

.sd-chart__fullscreen {
  display: flex;
  align-items: center;
  padding: 6px;
  font-size: 20px;
  color: #8b95a5;
  cursor: pointer;
}

.sd-components {
  padding: 12px 16px;
  margin-bottom: 10px;
  background-color: #fff;
}

.sd-components__header {
  margin-bottom: 12px;
}

.sd-components__title {
  font-size: 16px;
  font-weight: 700;
  color: #111e36;
}

.sd-components__table {
  overflow: hidden;
  border: 1px solid #eef0f4;
  border-radius: 8px;
}

.sd-components__thead {
  display: grid;
  grid-template-columns: 2fr 1fr 1.5fr;
  gap: 8px;
  padding: 12px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #eef0f4;
}

.sd-components__th {
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: flex-end;
  font-size: 12px;
  font-weight: 600;
  color: #8b95a5;
  text-align: right;
}

.sd-components__th--name {
  justify-content: flex-start;
  text-align: left;
}

.sd-components__th--sortable {
  cursor: pointer;
}

.sd-components__tbody {
  background-color: #fff;
}

.sd-components__tr {
  display: grid;
  grid-template-columns: 2fr 1fr 1.5fr;
  gap: 8px;
  padding: 12px;
  border-bottom: 1px solid #f1f2f7;

  &:last-child {
    border-bottom: none;
  }
}

.sd-components__td {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-end;
  justify-content: center;
  font-size: 13px;
  color: #111e36;
  text-align: right;
}

.sd-components__td--name {
  align-items: flex-start;
  text-align: left;
}

.sd-components__td--price {
  align-items: flex-end;
  justify-content: center;
  white-space: nowrap;
}

.sd-components__td--change {
  gap: 6px;
  align-items: flex-end;
  justify-content: center;
}

.sd-components__name {
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 13px;
  font-weight: 600;
  color: #111e36;
  white-space: nowrap;
}

.sd-components__code {
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 11px;
  color: #8b95a5;
  white-space: nowrap;
}

.sd-components__badge {
  display: inline-block;
  padding: 9px 10px 8px;
  font-family: PingFangSC-Medium;
  font-size: 14px;
  font-weight: 500;
  line-height: 14px;
  color: #fff;
  text-align: right;
  background-color: #e51a2d;
  border-radius: 4px;
}

.sd-components__badge.is-up {
  color: #fff;
  background-color: #22a875;
}

.sd-components__remove {
  padding: 4px 10px;
  font-size: 11px;
  color: #3c78ff;
  white-space: nowrap;
  cursor: pointer;
  background-color: transparent;
  border: 1px solid #3c78ff;
  border-radius: 4px;
}

.sd-navbar {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: space-around;
  padding: 10px 0;
  padding-bottom: calc(10px + env(safe-area-inset-bottom));
  background-color: #fff;
}

.sd-navbar__item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #666;
  cursor: pointer;
}

.sd-navbar__item :deep(.icon),
.sd-navbar__item :deep(svg) {
  width: 22px;
  height: 22px;
  font-size: 22px;
  color: #666;
  fill: #666;
}

.sd-navbar__item span {
  font-size: 12px;
  line-height: 1;
  color: #666;
}

.is-up {
  color: #22a875;
}

.is-down {
  color: #e51a2d;
}
</style>
